import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTASection from "@/components/sections/CTASection";
import { Reveal, StaggerGroup, staggerItem } from "@/components/motion/Reveal";
import { motion } from "framer-motion";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import GrowthRing from "@/components/visuals/GrowthRing";
import { brand } from "@/content/site";
import {
  Check,
  Zap,
  Target,
  BarChart3,
  ArrowRight,
  Info,
  ShieldCheck,
} from "lucide-react";

const heroImage =
  "https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const metrics = [
  { value: "4 weeks", label: "campaign live time" },
  { value: "$400/wk", label: "minimum Meta spend" },
  { value: "$1,600", label: "minimum total media" },
  { value: "$0", label: "Green Circle fee" },
];

const included = [
  "Growth diagnostic covering the offer, service area, competitors and current digital presence.",
  "One focused Meta campaign for one agreed priority product or offer.",
  "Campaign strategy, audience setup, ad copy and an agreed set of creative variations.",
  "Lead form or suitable landing-page pathway, tracking and lead delivery setup.",
  "Active campaign management, optimisation and a concise weekly performance review.",
  "End-of-pilot commercial review across leads, contact, qualification, appointments, quotes and sales outcomes.",
];

const journey = [
  {
    week: "01",
    title: "Launch & Learn",
    lead: "Client handles leads",
    desc: "Campaign goes live. You experience the raw lead flow, response and feedback. We capture baseline performance.",
  },
  {
    week: "02",
    title: "Refine",
    lead: "Client handles leads",
    desc: "We optimise audience, ad creative and offer based on early data—so week 3 and 4 give you clearer, higher-quality signal.",
  },
  {
    week: "03",
    title: "Conversion Assist",
    lead: "Green Circle optional",
    desc: "Green Circle can respond to new pilot leads using your approved offer, scripts, pricing and systems—fast contact, structured qualification and booking.",
  },
  {
    week: "04",
    title: "Prove & Review",
    lead: "Green Circle optional",
    desc: "Continued follow-up, then a joint commercial review across the full funnel—leads, contactable, qualified, appointments, quotes and sales outcomes.",
  },
];

const funnelStages = [
  "Leads",
  "Contactable",
  "Qualified",
  "Appointments",
  "Quotes",
  "Sales",
];

const conditions = [
  {
    title: "Budget & term",
    desc: "Minimum $400 per week for four consecutive live campaign weeks. Payment failure or a client-requested pause also pauses the pilot clock.",
  },
  {
    title: "Access & approvals",
    desc: "The client provides timely access to Meta assets, website or CRM pathways, brand assets, approved claims, pricing and campaign approvals.",
  },
  {
    title: "Offer & capacity",
    desc: "The offer must be commercially credible, serviceable in the agreed territory and supported by sufficient sales and installation capacity.",
  },
  {
    title: "Lead handling",
    desc: "During weeks 1 and 2, the client responds promptly and records outcomes so campaign quality can be separated from sales execution.",
  },
  {
    title: "Compliance",
    desc: "The retailer remains responsible for licences, product claims, pricing, incentives, consumer-law obligations, privacy and any NETCC representations.",
  },
  {
    title: "Conversion factors",
    desc: "Results are influenced by digital presence, reviews, market position, pricing, finance options, offer strength, response speed, sales capability and competition.",
  },
  {
    title: "Exclusions",
    desc: "Media spend, website rebuilds, CRM subscriptions, photography, extensive video production, extra territories and additional campaign streams are excluded unless agreed.",
  },
  {
    title: "No guarantee",
    desc: "Green Circle does not guarantee a fixed number of leads, appointments, sales or revenue. The purpose is to create and assess real market evidence.",
  },
];

export default function EnergyGrowthPilot() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Energy Growth Pilot",
    serviceType:
      "Four-week Meta campaign pilot for battery, solar and home energy retailers",
    provider: { "@type": "Organization", name: brand.name, url: brand.siteUrl },
    areaServed: "AU",
    description:
      "A four-week Meta campaign designed to test lead quality, conversion and scalability with no Green Circle management fee.",
  };

  return (
    <>
      <SEO
        title="Energy Growth Pilot | Four-week Meta pilot for solar, battery & heat pump retailers | Green Circle Group"
        description="A four-week Meta campaign designed to test lead quality, conversion and scalability. Client funds Meta media spend from $400/week. $0 Green Circle management fee."
        path="/energy-growth-pilot"
        image={heroImage}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        data-testid="egp-hero"
        className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24 bg-forest text-white"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-48 -top-32 opacity-40"
        >
          <GrowthRing size={520} duration={80} />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-8 [&_span]:!text-white/60 [&_a]:!text-white/60 hover:[&_a]:!text-white [&_.font-semibold]:!text-white">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "Energy Growth Pilot", to: "/energy-growth-pilot" },
              ]}
            />
          </div>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <Eyebrow dark>For Solar, Battery & Home Energy Retailers</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white text-balance">
                  A four-week pilot to test lead quality, conversion and scalability.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl">
                  You experience the leads first. We then demonstrate how
                  structured response, qualification and follow-up can move the
                  same campaign opportunities further.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Btn
                    to="/discovery/energy-growth-pilot"
                    size="lg"
                    data-testid="egp-hero-book-cta"
                  >
                    Book a Discovery Call
                  </Btn>
                  <Btn
                    href="#how-it-works"
                    variant="ghostLight"
                    size="lg"
                    withArrow
                  >
                    How the pilot works
                  </Btn>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="mt-8 flex flex-wrap gap-2.5">
                  {[
                    "4 weeks live",
                    "$0 management fee during the pilot",
                    "Client funds Meta media",
                    "Real commercial evidence",
                  ].map((c) => (
                    <li
                      key={c}
                      className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-semibold text-white"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                      {c}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            {/* Metric grid */}
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="rounded-[2rem] bg-white/5 border border-white/10 backdrop-blur p-6 md:p-8">
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-lime">
                    A meaningful pilot, not a token test
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    {metrics.map((m) => (
                      <div
                        key={m.label}
                        className="rounded-2xl bg-forest/60 border border-white/10 p-5"
                      >
                        <div className="text-2xl md:text-3xl font-extrabold text-lime">
                          {m.value}
                        </div>
                        <div className="mt-1 text-xs md:text-sm text-white/70 leading-snug">
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-sm text-white/70 leading-relaxed">
                    The budget and duration are deliberately set to give the
                    campaign enough room to learn, improve and produce useful
                    commercial evidence.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="how-it-works" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>The Offer</Eyebrow>
            <SectionHeading className="mt-5">
              Included at no Green Circle management cost
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              You fund Meta media spend directly to Meta at a minimum of $400
              per week. Green Circle does not mark up the media spend. The pilot
              is deliberately focused on one priority offer and one territory.
            </p>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 gap-5">
            {included.map((item) => (
              <motion.div
                key={item}
                variants={staggerItem}
                className="flex items-start gap-4 rounded-2xl border border-border-neutral bg-soft/50 p-6"
              >
                <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest text-lime">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <p className="text-forest leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </StaggerGroup>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-border-neutral p-6">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mint">
                <Target className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-extrabold text-forest">
                What the pilot proves
              </h3>
              <p className="mt-2 text-mid-neutral leading-relaxed">
                Whether the offer can attract relevant local enquiries at a
                sustainable cost—and where conversion is won or lost after the
                enquiry arrives.
              </p>
            </div>
            <div className="rounded-2xl border border-border-neutral p-6">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mint">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-extrabold text-forest">
                Keep the test focused
              </h3>
              <p className="mt-2 text-mid-neutral leading-relaxed">
                At $400 per week, focus on one priority offer and one territory.
                Depth over breadth produces cleaner evidence.
              </p>
            </div>
            <div className="rounded-2xl border border-border-neutral p-6">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mint">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-extrabold text-forest">
                Best suited to
              </h3>
              <p className="mt-2 text-mid-neutral leading-relaxed">
                Established retailers with an approved offer, capacity to
                service the agreed territory, prompt lead handling and
                market-aligned pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOUR-WEEK JOURNEY */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>The Four-Week Journey</Eyebrow>
            <SectionHeading className="mt-5">
              First you experience the leads. Then we test conversion support.
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              This sequence separates campaign quality from follow-up execution
              and gives both teams clearer evidence.
            </p>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {journey.map((j) => (
              <motion.div
                key={j.week}
                variants={staggerItem}
                className="relative rounded-[1.5rem] bg-white border border-border-neutral p-6 md:p-7"
              >
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-mint">
                  Week {j.week}
                </div>
                <h3 className="mt-2 text-xl font-extrabold text-forest">
                  {j.title}
                </h3>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-soft border border-border-neutral px-3 py-1 text-xs font-semibold text-forest">
                  <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                  {j.lead}
                </div>
                <p className="mt-4 text-mid-neutral leading-relaxed">{j.desc}</p>
              </motion.div>
            ))}
          </StaggerGroup>

          {/* Funnel */}
          <div className="mt-16 rounded-[2rem] bg-forest text-white p-8 md:p-10">
            <div className="flex items-start gap-3">
              <BarChart3 className="h-6 w-6 text-lime shrink-0 mt-1" />
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-lime">
                  Full commercial funnel
                </p>
                <h3 className="mt-1 text-2xl md:text-3xl font-extrabold">
                  Lead quality is measured end to end
                </h3>
                <p className="mt-3 text-white/80 leading-relaxed max-w-2xl">
                  The pilot report identifies the drop-off point, not just the
                  headline cost per lead.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {funnelStages.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <div className="rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-semibold">
                    {s}
                  </div>
                  {i < funnelStages.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-white/50" />
                  )}
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm text-white/70">
              <strong className="text-white">Client handover:</strong> Quotes,
              contracts and any regulated or product-specific representations
              remain with the retailer unless a broader sales scope is
              separately agreed.
            </p>
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Participation Conditions</Eyebrow>
            <SectionHeading className="mt-5">
              A fair test requires both teams to do their part
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              These conditions protect the integrity of the pilot and make the
              final review commercially useful.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {conditions.map((c) => (
              <Reveal
                key={c.title}
                className="rounded-2xl border border-border-neutral p-6 hover:border-mint transition-colors"
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-soft text-mint">
                  <Info className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-extrabold text-forest">
                  {c.title}
                </h3>
                <p className="mt-2 text-mid-neutral leading-relaxed">{c.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AFTER WEEK 4 */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Eyebrow className="justify-center">After Week 4</Eyebrow>
          <SectionHeading className="mt-5">
            Move from evidence to a managed growth engine
          </SectionHeading>
          <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
            We review the commercial funnel together. If there is a strong fit,
            the campaign transitions into Green Circle's Get Leads subscription,
            with ongoing management fees, media spend, deliverables and
            reporting confirmed in a separate proposal. Final scope is confirmed
            in the client proposal before launch.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Btn to="/discovery/energy-growth-pilot" size="lg" data-testid="egp-book-cta">
              Book a Discovery Call
            </Btn>
            <Btn
              to="/plans"
              variant="secondary"
              size="lg"
              withArrow
            >
              Compare Growth Plans
            </Btn>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Ready to test the market properly?"
        heading="Book a complimentary Discovery Call"
      />
    </>
  );
}
